import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../../calendario/services/laborScheduleService';
import {
  getCoverageMissingSkills,
  isCoverageFullyCovered,
} from '../../calendario/utils/laborPatternSkillsUtils';

/**
 * Banner de cobertura de skills para la vista de asistencia.
 */
export function LaborPatternCoverageBanner({ patternId, date, fetchEnabled = false }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['labor-pattern-coverage', patternId, date],
    queryFn: () => laborScheduleService.checkPatternCoverage(patternId, date),
    enabled: fetchEnabled && Boolean(patternId) && Boolean(date),
  });

  if (!patternId || !date || !fetchEnabled) return null;

  if (isLoading) {
    return (
      <div className="mb-4 flex items-center gap-2 text-gray-400 text-sm rounded-lg border border-gray-700 bg-gray-800/60 px-3 py-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Comprobando cobertura de habilidades…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4 flex items-center gap-2 text-red-300 text-sm rounded-lg border border-red-800 bg-red-900/20 px-3 py-2">
        <AlertCircle className="w-4 h-4 shrink-0" />
        No se pudo comprobar cobertura: {error.message}
      </div>
    );
  }

  if (isCoverageFullyCovered(data)) {
    return (
      <div className="mb-4 flex items-center gap-2 text-emerald-300 text-sm rounded-lg border border-emerald-800/50 bg-emerald-900/20 px-3 py-2">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        Cobertura de habilidades completa para este turno.
      </div>
    );
  }

  const missing = getCoverageMissingSkills(data);
  const patternName = data?.PatternName ?? data?.pattern_name;

  return (
    <div className="mb-4 rounded-lg border border-orange-800/50 bg-orange-900/20 px-3 py-2 text-sm text-orange-200">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">
            Infracobertura{patternName ? ` — ${patternName}` : ''}
          </p>
          {missing.length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-xs text-orange-300/90">
              {missing.map((gap, i) => (
                <li key={i}>· {gap}</li>
              ))}
            </ul>
          ) : (
            <p className="text-xs mt-1">Faltan empleados con las habilidades requeridas.</p>
          )}
        </div>
      </div>
    </div>
  );
}
